import {
  EmailLogLanguage,
  IEmailLogApiClient,
  IMixpanelConfig,
  IUserInfo,
  AdvancedSearchCapability
} from '@barracuda-internal/converged-email-log'

import { useEffect, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'redux/toolkit/hooks'

import { useEmailLogApiClient } from 'components/pages/emailLog/useEmailLogApiClient'
import { isSuccess, isFailed, isIdle } from 'redux/toolkit/api'
import { EGD_PRODUCT_ID, IP_PRODUCT_ID, IR_PRODUCT_ID, getPamProductData } from 'redux/features/pam/pamSlice'
import type { PamProductDataResponse } from 'redux/features/pam/pamApiThunks'
import appConfig from 'config/appConfig'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import routesConfig from 'lib/routesConfig'
import { User } from 'types/auth'
import { Language } from 'types/Language'
import { useApi } from 'v2/modules/lib/hooks/useApi'
import { Auth } from 'v2/modules/lib'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

export interface UseEmailLogLogic {
  bccAccountId: string
  emailLogApiClient: IEmailLogApiClient
  emailLogLanguage: EmailLogLanguage
  mixpanelConfig: IMixpanelConfig
  shouldShowMultiAccountSearch: boolean
  userInfo: IUserInfo
  baseUrl: string
  basePath: string
  envVars: Record<string, string>
  enableEmailPolicies: boolean
  enableEmailPoliciesRiskyConfig: boolean
  enableMultiAccountRiskyPolicy: boolean
  aiServiceFlagEnabled: boolean
  advancedSearchCapability: AdvancedSearchCapability
  gatewayUrl: string
}

export type UiStateInitializing = { state: 'initializing' }
export type UiStateLoading = { state: 'loading'; data: { accessTokenObject: User; language: string } }
export type UiStateReady = { state: 'ready'; data: UseEmailLogLogic }
export type UiStateError = { state: 'error'; error: Error }
export type UiState = UiStateInitializing | UiStateLoading | UiStateReady | UiStateError

/**
 * Computes Advanced Search capability from feature flag and PAM product data.
 * - Feature flag OFF → disabled (feature-flag-off)
 * - PAM failed → disabled (unknown)
 * - Has products 6+20+25 (EGD+IP+IR) → egd-ices → advanced-search
 * - Otherwise → egd-only → simple-filter
 *
 * Note: This helper is intentionally local to this file. It is not intended for reuse
 * by other parts of EGD. It lives here due to EP repo limitations (capability logic
 * must be computed host-side, but EP cannot access PAM/feature-flag infrastructure).
 */
function computeAdvancedSearchCapability(
  featureFlagEnabled: boolean,
  pamProductData: PamProductDataResponse | undefined,
  pamFailed: boolean
): AdvancedSearchCapability {
  if (!featureFlagEnabled) {
    return { state: 'disabled', reason: 'feature-flag-off' }
  }

  if (pamFailed) {
    return { state: 'disabled', reason: 'unknown' }
  }

  // Collect all product IDs in a single pass for efficient membership checking
  const productIds = new Set<string>(
    (pamProductData?.data?.subscriptionGroups ?? []).flatMap(group =>
      (group.subscriptions ?? []).map(subscription => subscription.product.id)
    )
  )

  const hasEgd = productIds.has(EGD_PRODUCT_ID)
  const hasIp = productIds.has(IP_PRODUCT_ID)
  const hasIr = productIds.has(IR_PRODUCT_ID)

  if (hasEgd && hasIp && hasIr) {
    return { state: 'advanced-search', accountType: 'egd-ices' }
  }

  return { state: 'simple-filter', accountType: 'egd-only' }
}

export const useEmailLogLogic = (): UiState => {
  const dispatch = useAppDispatch()
  const canSetState = useRef(true)
  const [uiState, setUiState] = useState<UiState>({ state: 'initializing' })
  const { accessTokenObject, language, accessToken, pamProductData, pamApiStatus } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    accessToken: _stores.auth.accessToken,
    language: _stores.app.language,
    pamProductData: _stores.pam.productData,
    pamApiStatus: _stores.pam.api.getProductData
  }))
  const emailLogApiClient = useEmailLogApiClient()
  const api = useApi({
    host: appConfig.GATEWAY_URL ? `${appConfig.GATEWAY_URL}/v2` : '',
    accessToken: accessToken || ''
  })

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  // Ensure PAM product data is fetched if not already requested
  // Only dispatch when truly uninitialized (inIdle = [Idle] with no response status)
  // Note: isIdle() returns true for successResponse/failedResponse too since they start with Idle
  useEffect(() => {
    const isTrulyUninitialized = pamApiStatus?.length === 1 && isIdle(pamApiStatus)
    if (isTrulyUninitialized) {
      dispatch(getPamProductData())
    }
  }, [dispatch, pamApiStatus])

  // PAM terminal state (success or failed means we can proceed)
  const isPamTerminal = isSuccess(pamApiStatus) || isFailed(pamApiStatus)

  // This effect waits for redux dependencies to be set, then sets uiState to loading
  // to fetch data depending on redux dependencies (accessTokenObject & language)
  // Also waits for PAM data to reach terminal state
  useEffect(() => {
    if (!accessTokenObject || !language || !canSetState.current) {
      return
    }
    if (!isPamTerminal) {
      return
    }
    setUiState({ state: 'loading', data: { accessTokenObject, language } })
  }, [accessTokenObject, language, isPamTerminal])

  // This effect asynchronously prepares the props needed for rendering the EmailLogProvider
  useEffect(() => {
    if (uiState.state !== 'loading') {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      try {
        // Validate required config
        if (!appConfig.GATEWAY_URL) {
          throw new Error('GATEWAY_URL is not configured')
        }
        const gatewayUrl = appConfig.GATEWAY_URL

        const abortController = new AbortController()

        // Fetch all accounts and filter by EMAIL_PROTECTION_ACCESS policy
        // (No service filtering - we filter locally using Auth lib per reviewer guidance)
        const allUserAccounts = await emailLogApiClient.getBccUserAccounts(abortController)({
          bcc_user_id: uiState.data.accessTokenObject.userId
        })

        // Reuse policies from Redux (fetched at login) instead of making another network call
        const authPolicies = uiState.data.accessTokenObject.authDomainPolicies || []
        const auth = Auth(authPolicies)
        const userAccounts = (allUserAccounts || []).filter(account =>
          auth.hasEmailProtectionAccess(account.bcc_account_id)
        )

        const featureFlags = await api.bccAccountService.getAccountFeatures(
          uiState.data.accessTokenObject.bccAccountId,
          [
            'FEATURE_FLAG_EMAIL_POLICIES_ENABLED',
            'FEATURE_FLAG_EMAIL_POLICIES_RISKY_CONFIG_ENABLED',
            'FEATURE_FLAG_EMAIL_POLICIES_MULTI_ACCOUNT_RISKY_POLICY_ENABLED',
            'FEATURE_FLAG_AI_SERVICE_ENABLED',
            'FEATURE_FLAG_ADVANCED_SEARCH_ENABLED'
          ]
        )
        const enableEmailPolicies = featureFlags.feature_flags.FEATURE_FLAG_EMAIL_POLICIES_ENABLED
        const enableEmailPoliciesRiskyConfig =
          featureFlags.feature_flags.FEATURE_FLAG_EMAIL_POLICIES_RISKY_CONFIG_ENABLED
        const enableMultiAccountRiskyPolicy =
          featureFlags.feature_flags.FEATURE_FLAG_EMAIL_POLICIES_MULTI_ACCOUNT_RISKY_POLICY_ENABLED
        const aiServiceFlagEnabled = featureFlags.feature_flags.FEATURE_FLAG_AI_SERVICE_ENABLED
        const advancedSearchFlagEnabled = featureFlags.feature_flags.FEATURE_FLAG_ADVANCED_SEARCH_ENABLED

        const pamFailed = isFailed(pamApiStatus)
        const advancedSearchCapability = computeAdvancedSearchCapability(
          advancedSearchFlagEnabled,
          pamProductData,
          pamFailed
        )
        const mixpanelConfig = {
          product: 'EP',
          distinctId: uiState.data.accessTokenObject.userId,
          enabled: appConfig.MIXPANEL.IS_ENABLED,
          profileProperties: {
            serial: uiState.data.accessTokenObject.serial,
            account_id: uiState.data.accessTokenObject.accountId,
            user_id: uiState.data.accessTokenObject.userId,
            name: uiState.data.accessTokenObject.userInfo?.name,
            accounts: uiState.data.accessTokenObject.userInfo?.accounts || [],
            email: uiState.data.accessTokenObject.userId
          }
        }
        const emailLogLanguage = ((): EmailLogLanguage => {
          const isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)

          switch (language) {
            case Language.japanese:
              return EmailLogLanguage.JP
            case Language.spanish:
              return EmailLogLanguage.ES
            case Language.french:
              return isSmartlingEnabled ? EmailLogLanguage.FR : EmailLogLanguage.EN
            case Language.italian:
              return isSmartlingEnabled ? EmailLogLanguage.IT : EmailLogLanguage.EN
            case Language.german:
              return isSmartlingEnabled ? EmailLogLanguage.DE : EmailLogLanguage.EN
            case Language.dutch:
              return isSmartlingEnabled ? EmailLogLanguage.NL : EmailLogLanguage.EN
            case Language.english:
            default:
              return EmailLogLanguage.EN
          }
        })()
        const basePath = routesConfig.EMAIL_LOG.path
        const baseUrl = `${window.origin}${basePath}`
        const data: UseEmailLogLogic = {
          bccAccountId: uiState.data.accessTokenObject.bccAccountId,
          emailLogApiClient,
          emailLogLanguage,
          mixpanelConfig,
          shouldShowMultiAccountSearch: (userAccounts || []).length > 1,
          userInfo: {
            bccUserId: uiState.data.accessTokenObject.userId,
            accounts: (userAccounts || []).map(account => ({
              bccAccountId: account.bcc_account_id,
              bccAccountName: account.bcc_account_name,
              egdAccountId: account.egd_account_id,
              egdAccountRegion: account.egd_account_region
            }))
          },
          basePath,
          baseUrl,
          envVars: {},
          enableEmailPolicies,
          enableEmailPoliciesRiskyConfig,
          enableMultiAccountRiskyPolicy,
          aiServiceFlagEnabled,
          advancedSearchCapability,
          gatewayUrl
        }
        if (!canSetState.current) {
          return
        }
        setUiState({ state: 'ready', data })
        trackEventInAllServices(TRACKING_EVENTS.EMAIL_LOG.CONVERGED_EMAIL_PAGEVIEW)
      } catch (err) {
        if (!canSetState.current) {
          return
        }
        setUiState({ state: 'error', error: err instanceof Error ? err : new Error(String(err)) })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.state])

  return uiState
}
